import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { PrintProofReviewDto2, PrintProofReviewResultEnum } from '@/api-sdk';
import { iconCheckmarkCircle, iconCircleX } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_LARGE_PX } from '@/settings';
import { baseStyles, buttonStyles, printProofStyles } from '@/styles';
import { formatDate } from '@/utils';
import { openPrintProofDialog } from '../utils';

@customElement('fg-configuration-print-reviewed')
@localized()
export class FgConfigurationPrintProcessed extends LitElement {
  @property({ type: Object })
  reviewStatus?: PrintProofReviewDto2 | null;

  @consume({ context: global.context, subscribe: true })
  globalContext!: GlobalComponentType;

  render() {
    if (!this.reviewStatus) {
      return nothing;
    }
    const { result, timestamp } = this.reviewStatus;

    const resultContent = {
      [PrintProofReviewResultEnum.approved]: {
        string: t('content.printProof.statusApproved'),
        icon: iconCheckmarkCircle,
      },
      [PrintProofReviewResultEnum.rejected]: {
        string: t('content.printProof.statusRejected'),
        icon: iconCircleX,
      },
    };

    return html`
      <div class="status ${result}">
        <div class="message">
          ${resultContent[result].icon}
          <span>
            ${resultContent[result].string}
            <time>${formatDate(timestamp, this.globalContext.locale)}</time>
          </span>
        </div>
        <button @click=${() => openPrintProofDialog(this)}>
          ${t('actions.view')}
        </button>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    printProofStyles,
    css`
      .status {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }

      :host([part='wholesale']) .status {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .status.approved svg {
        color: var(--fg-success, var(--fg-fallback-success));
      }

      :host([part='wholesale']) .status.approved {
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .status.rejected svg {
        fill: var(--fg-error, var(--fg-fallback-error));
      }

      :host([part='wholesale']) .status.rejected {
        background-color: var(
          --fg-error--light,
          var(--fg-fallback-error--light)
        );
        border: 1px solid var(--fg-error, var(--fg-fallback-error));
      }

      .status button {
        text-decoration: underline;
      }

      .status button:hover {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }

      .status button:focus-visible {
        outline: 2px solid var(--fg-gray--800, var(--fg-fallback-gray--800));
      }

      .message {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      @media screen and (max-width: ${BREAKPOINT_LARGE_PX}px) {
        .status button {
          margin: 0 auto;
        }
      }
    `,
  ];
}
